import type { Lesson, QuizQuestion, GlossaryItem } from "../../lib/types";

export const LESSON_FOUNDATIONS: Lesson = {
  courseId: "foundations",
  courseTitle: "Git, in plain English",
  id: "lesson-1",
  title: "Git, in plain English",
  subtitle: "Lesson 1 of 4",
  steps: [
    {
      id: "history",
      shortTitle: "Version history, but better",
      shortSub: "The mental model",
      eyebrow: "Step 1 of 4",
      title: "You already use version control.",
      sub: "Every time you've reverted a Google Doc, restored an old slide, or kept a folder called \"final-v3-REAL.docx\" — that's version control. Git is the same idea, made reliable, and built so a whole team can use it at once.",
      kind: "concept",
      diagram: "history",
    },
    {
      id: "why-git",
      shortTitle: "Why git specifically",
      shortSub: "The industry standard",
      eyebrow: "Step 2 of 4",
      title: "Why git won.",
      sub: "Before git, most teams used a central server — if it went down, nobody could work. Git gave every person a full copy of the project's history on their own laptop. That one design choice made it fast, resilient, and the standard everywhere.",
      kind: "concept",
      diagram: "distributed",
    },
    {
      id: "three-places",
      shortTitle: "The three places",
      shortSub: "Where your work lives",
      eyebrow: "Step 3 of 4",
      title: "Your work lives in three places.",
      sub: "Your working files on your laptop, your local git history (also on your laptop), and the shared copy on GitHub. Most confusion in git comes from not knowing which of the three you're looking at.",
      kind: "concept",
      diagram: "three-places",
    },
    {
      id: "wrap",
      shortTitle: "Quiz & recap",
      shortSub: "Knowledge check",
      eyebrow: "Wrap-up",
      title: "You've got the mental model.",
      sub: "Quick check below. No commands yet — those start in the next course, where you'll actually make commits and branches in a live sandbox.",
      kind: "wrap",
    },
  ],
};


export const QUIZ_QUESTIONS: QuizQuestion[] = [
  {
    q: "In plain English, what is git's job?",
    options: [
      { t: "To compress files so they take less space", correct: false },
      {
        t: "To remember every meaningful version of your project, who changed what, and why",
        correct: true,
      },
      { t: "To host your code on the internet", correct: false },
      { t: "To check your code for bugs before you ship", correct: false },
    ],
    why: "Right — git is a version-history system. Hosting (GitHub), code review, and CI/CD are separate things built on top.",
    wrongWhy:
      "Git's core job is version history — remembering every saved version of every file with a label saying who changed what and why. Hosting (GitHub) and bug-checking are separate tools built around it.",
  },
  {
    q: "What does it mean that git is \"distributed\"?",
    options: [
      { t: "It's spread across many servers run by a company", correct: false },
      {
        t: "Every person working on the project has a full copy of the history on their laptop",
        correct: true,
      },
      { t: "It splits files into pieces stored in different places", correct: false },
      { t: "It runs in the cloud instead of on your computer", correct: false },
    ],
    why: "Exactly. Distributed = every collaborator has a full copy. You can work offline, experiment privately, and only share when you choose to.",
    wrongWhy:
      'Distributed in git means each contributor has a full local copy of the project\'s history — not split storage, not "in the cloud." That\'s what lets you work offline and only share when you\'re ready.',
  },
  {
    q: "Which three places does your work live when you use git with GitHub?",
    options: [
      { t: "Drafts, published, archived", correct: false },
      {
        t: "Working tree on your laptop, local git history on your laptop, remote on GitHub",
        correct: true,
      },
      { t: "Your editor, your terminal, your browser", correct: false },
      { t: "Development, staging, production", correct: false },
    ],
    why: "Yes — the three places. Most git confusion comes from not knowing which of the three you're currently looking at.",
    wrongWhy:
      "The three places are: the files you can edit (working tree), the version history saved on your laptop (local repo), and the shared copy on GitHub (remote). Knowing which you're in is the key to not getting lost.",
  },
];

export const GLOSSARY: GlossaryItem[] = [
  {
    term: "Version control",
    def: "A system that remembers every saved version of every file in a project, along with who changed what and why.",
  },
  {
    term: "Git",
    def: "The version-control tool used by nearly every software team today. Free, fast, distributed.",
  },
  {
    term: "Distributed",
    def: "Every person collaborating has a full copy of the project's history on their own machine — not just a connection to a central server.",
  },
  {
    term: "GitHub",
    def: "A website that hosts git projects and adds collaboration tools (pull requests, code review, issues). Git ≠ GitHub.",
  },
  {
    term: "Working tree",
    def: "The files on your laptop as you currently see them — including any unsaved edits.",
  },
  {
    term: "Local repo",
    def: "The full project history kept on your laptop in a hidden .git folder. Yours alone, until you share it.",
  },
  {
    term: "Remote",
    def: "The shared copy of the repo — usually on GitHub — that the whole team syncs with.",
  },
];
